import { createSlice, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { showApiErrors } from '@/utils';
import { initialState, CATEGORIES_SLICE_NAME, CategoriesState } from './models';
import {
  createCategory,
  createFilter,
  createPopularCategories,
  deleteCategory,
  toggleActiveCategory,
  deleteFilter,
  deleteFilterValue,
  editCategory,
  editFilter,
  getCategories,
  getCategoryDetails,
  getCategoryStats,
  getFilterData,
  getFilters,
  getCategoryFiltersList,
  getFilterValues,
  getPopularCategories,
  getSubCategories,
  updateCategoryFilters,
  getCategoriesAutocomplete,
  editFilterValue,
  createFilterValue,
} from './actionCreators';
import { removeCategoryById } from '../helpers/removeCategoryById';
import { TGetFiltersQuery, TGetFiltersValuesQuery } from '../helpers/types';
import { IGetCategoryStatsDTO } from '@/models/categories.model';

export const slice = createSlice({
  name: CATEGORIES_SLICE_NAME,
  initialState,
  reducers: {
    resetCategoriesState() {
      return { ...initialState };
    },
    changeQuery: (state: CategoriesState, action: PayloadAction<TGetFiltersQuery>) => {
      state.filtersQuery = action.payload;
    },
    changeFilterValuesQuery: (state: CategoriesState, action: PayloadAction<TGetFiltersValuesQuery>) => {
      state.filtersValuesQuery = action.payload;
    },
    changeCategoryStatsQuery: (state: CategoriesState, action: PayloadAction<IGetCategoryStatsDTO>) => {
      state.categoryStatsQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCategories.fulfilled, (state: CategoriesState, action) => {
        state.categories = action.payload;
        state.isLoadingCategories = false;
      })
      .addCase(getCategoriesAutocomplete.fulfilled, (state: CategoriesState, action) => {
        state.categoriesAutocomplete = action.payload;
        state.isLoadingCategories = false;
      })
      .addCase(getPopularCategories.fulfilled, (state: CategoriesState, action) => {
        state.popularCategories = action.payload;
        state.isLoadingCategories = false;
      })
      .addCase(createPopularCategories.fulfilled, (state: CategoriesState) => {
        state.isLoadingCategories = false;
      })
      .addCase(getSubCategories.fulfilled, (state: CategoriesState, action) => {
        categoriesLoop: for (const category of state.categories!) {
          for (const subCategory of category.sub_categories || []) {
            if (subCategory.id === action.payload.data.id) {
              subCategory.sub_categories = action.payload.data.sub_categories || [];
              subCategory.is_loading = false;
              break categoriesLoop;
            }
          }
        }
      })
      .addCase(getCategoryDetails.fulfilled, (state: CategoriesState, action) => {
        state.categoryDetails = action.payload;
        state.isLoadingCategoryDetails = false;
      })
      .addCase(deleteCategory.fulfilled, (state: CategoriesState, action) => {
        if (action.payload.data.success) {
          const idToDelete = action.meta.arg;

          state.categories = removeCategoryById(state.categories!, idToDelete);
          state.isLoadingCategories = false;
        }
      })
      .addCase(getFilters.fulfilled, (state: CategoriesState, action) => {
        state.filters = action.payload;
        state.isLoadingFilters = false;
      })
      .addCase(getCategoryFiltersList.fulfilled, (state: CategoriesState, action) => {
        state.filtersList = action.payload;
        state.isLoadingFiltersList = false;
      })
      .addCase(updateCategoryFilters.fulfilled, (state: CategoriesState, action) => {
        state.categoryDetails = action.payload;
        state.isLoadingCategoryFiltersList = false;
      })
      .addCase(getFilterValues.fulfilled, (state: CategoriesState, action) => {
        state.filterValues = action.payload;
        state.isLoadingFilters = false;
      })
      .addCase(editCategory.fulfilled, (state: CategoriesState, action) => {
        state.categoryDetails = action.payload;
        state.isLoadingCategoryDetails = false;
      })
      .addCase(deleteFilter.fulfilled, (state: CategoriesState, action) => {
        if (!state.filters) return;
        state.filters.data = state.filters.data.filter((filter) => filter.id !== action.meta.arg);
      })
      .addCase(deleteFilterValue.fulfilled, (state: CategoriesState, action) => {
        if (!state.filterValues) return;
        state.filterValues.data = state.filterValues.data.filter((filter) => filter.id !== action.meta.arg.id);
      })
      .addCase(getCategoryStats.fulfilled, (state: CategoriesState, action) => {
        state.categoryStats = action.payload;
        state.isLoading = false;
      })
      .addCase(getCategoryStats.pending, (state: CategoriesState) => {
        state.isLoading = true;
      })
      .addCase(getFilterData.fulfilled, (state: CategoriesState, action) => {
        state.filterDetails = action.payload?.data;
        state.isLoadingFilterDetails = false;
      })
      .addCase(editFilter.fulfilled, (state: CategoriesState, action) => {
        if (!state.filters) return;
        state.filters.data = state.filters.data.map((filter) => {
          if (filter.id === action.meta.arg.id) return action.payload.data;
          return filter;
        });
      })
      .addCase(getCategories.pending, (state: CategoriesState) => {
        state.isLoadingCategories = true;
        state.error = null;
      })
      .addCase(getPopularCategories.pending, (state: CategoriesState) => {
        state.isLoadingCategories = true;
        state.error = null;
      })
      .addCase(editCategory.pending, (state: CategoriesState) => {
        state.isLoadingCategoryDetails = true;
        state.error = null;
      })
      .addCase(createPopularCategories.pending, (state: CategoriesState) => {
        state.isLoadingCategories = true;
        state.error = null;
      })
      .addCase(getFilterData.pending, (state: CategoriesState) => {
        state.isLoadingFilterDetails = true;
        state.error = null;
      })
      .addCase(getSubCategories.pending, (state: CategoriesState, action) => {
        categoriesLoop: for (const category of state.categories!) {
          for (const subCategory of category.sub_categories || []) {
            if (subCategory.id === action.meta.arg) {
              subCategory.is_loading = true;
              break categoriesLoop;
            }
          }
        }
      })
      .addCase(getCategoryDetails.pending, (state: CategoriesState) => {
        state.isLoadingCategoryDetails = true;
      })
      .addCase(getFilters.pending, (state: CategoriesState) => {
        state.isLoadingFilters = true;
      })
      .addCase(getCategoryFiltersList.pending, (state: CategoriesState) => {
        state.isLoadingFiltersList = true;
      })
      .addCase(updateCategoryFilters.pending, (state: CategoriesState) => {
        state.isLoadingCategoryFiltersList = true;
      })
      .addCase(createCategory.pending, (state: CategoriesState) => {
        state.isLoadingCategories = true;
      })
      .addCase(createCategory.fulfilled, (state: CategoriesState) => {
        state.isLoadingCategories = false;
      })
      .addMatcher(
        isPending(getCategoriesAutocomplete, deleteCategory, toggleActiveCategory),
        (state: CategoriesState) => {
          state.isLoadingCategories = true;
        },
      )
      .addMatcher(
        isRejected(
          getCategories,
          getPopularCategories,
          createPopularCategories,
          getSubCategories,
          getCategoryDetails,
          getCategoryStats,
          createCategory,
          deleteCategory,
          editCategory,
          getFilters,
          getCategoryFiltersList,
          updateCategoryFilters,
          getFilterData,
          deleteFilter,
          getCategoriesAutocomplete,
          createFilter,
          editFilter,
          getFilterValues,
          editFilterValue,
          createFilterValue,
          toggleActiveCategory,
        ),
        (state: CategoriesState, action) => {
          const { error } = action;
          state.isLoadingCategories = false;
          state.isLoadingCategoryDetails = false;
          state.isLoadingFilterDetails = false;
          state.isLoadingFiltersList = false;
          state.isLoadingCategoryFiltersList = false;
          state.isLoading = false;
          state.error = error;

          showApiErrors(error);
        },
      );
  },
});

export const { resetCategoriesState, changeQuery, changeFilterValuesQuery, changeCategoryStatsQuery } = slice.actions;

export default slice.reducer;
